import React from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { getSellerBundleOffer } from '../../util/userHelpers';
import { richText } from '../../util/richText';


import { H3, NamedLink } from '../../components';
import css from './ShopAboutSection.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

/**
 * ShopAboutSection displays shop information including bio,
 * specialties, province, and bundle offer.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.user} props.seller - The seller user
 * @param {PropTypes.object} props.queryReviewsError - Error loading reviews
 * @returns {JSX.Element}
 */
const ShopAboutSection = props => {
  const { seller, queryReviewsError, isCurrentUser } = props;
  const intl = useIntl();

  const { bio, publicData = {} } = seller?.attributes?.profile || {};
  const shopBio = publicData.shopBio;
  const province = publicData.province;
  const shopSpecialties = publicData.shopSpecialties;
  const bundleOffer = getSellerBundleOffer(seller);

  const memberSinceDate = new Date(seller?.attributes?.createdAt);
  const memberSinceYear = memberSinceDate.getFullYear();

  const shopBioWithLinks = shopBio
    ? richText(shopBio, {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
      })
    : null;

  return (
    <div className={css.root}>
      {isCurrentUser ? (
        <NamedLink className={css.editLink} name="ProfileSettingsPage">
          <FormattedMessage id="ShopAboutSection.editAbout" />
        </NamedLink>
      ) : null}

      {queryReviewsError ? (
        <p className={css.error}>
          <FormattedMessage id="ShopAboutSection.loadingError" />
        </p>
      ) : null}

      {shopBioWithLinks ? (
        <div className={css.section}>
          <H3 className={css.sectionHeading}>
            <FormattedMessage id="ShopAboutSection.bioHeading" />
          </H3>
          <p className={css.bio}>{shopBioWithLinks}</p>
        </div>
      ) : null}

      {province ? (
        <div className={css.section}>
          <H3 className={css.sectionHeading}>
            <FormattedMessage id="ShopAboutSection.provinceHeading" />
          </H3>
          <p className={css.text}>{province}</p>
        </div>
      ) : null}

      {memberSinceYear ? (
        <div className={css.section}>
          <H3 className={css.sectionHeading}>
            <FormattedMessage id="ShopAboutSection.memberSince" />
          </H3>
          <p className={css.text}>{memberSinceYear}</p>
        </div>
      ) : null}

      {shopSpecialties && Array.isArray(shopSpecialties) && shopSpecialties.length > 0 ? (
        <div className={css.section}>
          <H3 className={css.sectionHeading}>
            <FormattedMessage id="ShopAboutSection.specialtiesHeading" />
          </H3>
          <div className={css.specialtiesList}>
            {shopSpecialties.map(specialty => (
              <span key={specialty} className={css.specialtyTag}>
                {specialty}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {bundleOffer ? (
        <div className={css.section}>
          <H3 className={css.sectionHeading}>
            <FormattedMessage id="ShopAboutSection.bundleOfferHeading" />
          </H3>
          <p className={css.text}>{bundleOffer}</p>
        </div>
      ) : null}

      {!shopBioWithLinks &&
        !province &&
        !memberSinceYear &&
        (!shopSpecialties || shopSpecialties.length === 0) &&
        !bundleOffer ? (
        <p className={css.emptyState}>
          <FormattedMessage id="ShopAboutSection.empty" />
        </p>
      ) : null}
    </div>
  );
};

ShopAboutSection.propTypes = {
  seller: propTypes.user.isRequired,
  queryReviewsError: PropTypes.object,
  isCurrentUser: PropTypes.bool,
};

ShopAboutSection.defaultProps = {
  isCurrentUser: false,
};

export default ShopAboutSection;
